<!-- L'enveloppe porte exactement ce que `<html>` portait dans le document
     autonome : thème, langue, direction et `has-js`. Toute la feuille du
     rapport est portée sous `.s2e-report`, ce qui l'empêche de fuir dans le
     reste de l'application. -->
<div
  class="s2e-report has-js"
  [attr.data-theme]="chrome.theme()"
  [attr.lang]="chrome.lang()"
  [attr.dir]="chrome.dir()"
>
  @if (loading()) {
    <div class="s2e-report-state" role="status" aria-live="polite">
      <i class="fa-solid fa-spinner fa-spin s2e-report-state__icon" aria-hidden="true"></i>
      <p class="s2e-report-state__text">{{ 'LOADINGS.DEFAULT' | translate }}</p>
    </div>
  } @else if (errored()) {
    <div class="s2e-report-state" role="alert">
      <i class="fa-solid fa-triangle-exclamation s2e-report-state__icon" aria-hidden="true"></i>
      <p class="s2e-report-state__title">{{ 'DIAGNOSTIC_REPORT.TITLE' | translate }}</p>
      <p class="s2e-report-state__text">{{ 'DIAGNOSTIC_REPORT.LOAD_ERROR' | translate }}</p>
    </div>
  } @else if (empty()) {
    <div class="s2e-report-state">
      <i class="fa-solid fa-file-circle-question s2e-report-state__icon" aria-hidden="true"></i>
      <p class="s2e-report-state__title">{{ 'DIAGNOSTIC_REPORT.TITLE' | translate }}</p>
      <p class="s2e-report-state__text">{{ 'DIAGNOSTIC_REPORT.NO_DATA' | translate }}</p>
    </div>
  } @else {
    <!-- `@else if` n'accepte pas d'alias `as` : d'où ce `@if` imbriqué, qui
         évite de répéter `view()!` dans tout le document. -->
    @if (view(); as v) {
    <!-- Page de garde du PDF. Hors du tableau ci-dessous, pour que les
         bandeaux courants ne commencent qu'à la deuxième feuille. -->
    <s2e-report-print-cover [view]="v" />

    <!-- `display: block` à l'écran, vrai tableau seulement en impression :
         c'est ainsi qu'on obtient un en-tête et un pied de page courants sur
         chaque feuille, ce qu'aucune autre mise en page n'offre. -->
    <table class="print-sheet">
      <thead>
        <tr>
          <td>
            <div class="print-runner print-runner--header" aria-hidden="true">
              <span class="brand-logo brand-logo--runner">
                <img
                  class="brand-logo__img brand-logo__img--ink"
                  src="assets/images/report/logo-skill2era-dark.png"
                  alt="Skill2era"
                  width="447"
                  height="178"
                />
                <img
                  class="brand-logo__img brand-logo__img--paper"
                  src="assets/images/report/logo-skill2era.png"
                  alt=""
                  aria-hidden="true"
                  width="447"
                  height="178"
                />
              </span>
              <div class="print-runner__text">
                <p class="print-runner__title">{{ 'app.title' | reportT }}</p>
                <p class="print-runner__meta">
                  <span class="print-runner__student">{{ v.header.studentName }}</span>

                  @if (v.header.testTitle) {
                    <span class="print-runner__sep">·</span>
                    <span>{{ v.header.testTitle }}</span>
                  }

                  @if (v.header.completedAt; as completedAt) {
                    <span class="print-runner__sep">·</span>
                    <span>{{ completedAt | reportFmt: 'date' }}</span>
                  }
                </p>
              </div>
            </div>
          </td>
        </tr>
      </thead>

      <tfoot>
        <tr>
          <td>
            <div class="print-runner print-runner--footer" aria-hidden="true">
              <span>{{ 'app.title' | reportT }}</span>
              <span class="print-runner__bullet">•</span>
              <span>{{ 'app.brand' | reportT }}</span>
            </div>
          </td>
        </tr>
      </tfoot>

      <tbody>
        <tr>
          <td>
            <div class="app">
              <a class="skip-link no-print" href="#report-content">
                {{ 'app.skipToContent' | reportT }}
              </a>

              <s2e-report-header
                [view]="v"
                [activeSubjectId]="activePanelId()"
                (subjectSelected)="onSubjectSelected($event)"
                (backRequested)="goBack()"
              />

              <main id="report-content" class="container report-main">
                <!-- Un panneau par matière : le document d'origine, répété.
                     Une seule matière en portée « matière », toutes en portée
                     complète — et toutes au tirage, dans les deux cas. -->
                @for (subject of v.subjects; track subject.subjectId) {
                  <s2e-report-subject-panel
                    [subject]="subject"
                    [scale]="v.gradeLevelScale"
                    [planWeeks]="v.planWeeks"
                    [visible]="subject.subjectId === activePanelId()"
                    [showTitle]="v.scope === 'all'"
                  />
                }

                <!-- Les deux seuls blocs d'ensemble, une fois, après la
                     dernière matière : ils comparent les matières entre elles
                     et n'auraient aucun sens répétés dans chaque panneau. -->
                @if (v.crossSubject; as cross) {
                  <s2e-report-all-subjects [cross]="cross" />
                  <s2e-report-comparison [cross]="cross" />
                }

                <s2e-report-footer [view]="v" />
              </main>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Surface d'infobulle unique, partagée par les six graphiques. -->
    <s2e-chart-tooltip />
    }
  }
</div>
